// ── 상황판 기능 카드 런처 (2026-08-25 사용자 명세) ──────────────────────────────
// 이미지 시안 기반: 색 박스 최소화(흰 카드+얇은 테두리), 카드 = 아이콘/제목/한 줄 설명/바로가기.
// 상단 검색 + "전체·카테고리" 탭(사이드바 9카테고리 재사용, 항목 있는 카테고리만 노출).
// 항목 = 본인이 보유한 사이드바 메뉴 그대로(관리 메뉴). "~ 열람" 카드는 2026-09-15 사이드바와 함께 제거.
// 기존 데이터 위젯은 이 아래에 그대로 공존(구조 (b), 사용자 확정).
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useAppPermissions } from '../../../hooks/useAppPermissions';
import { ADMIN_ONLY_MENUS } from '../adminlayout/menuConfig';
import { isAdminUser, filterAdminOnlyMenus } from '../adminlayout/sidebarFilters';
// 회사 선택창 (2026-09-03 개편 1차): 홈 카드도 선택 회사의 메뉴 세트를 따른다
import { useCompanyScope } from '../adminlayout/CompanyScopeContext';
// 메뉴 조립·분류는 사이드바와 한 곳에서 (2026-09-15). 전에는 로직이 복사돼 있어서 "고객 테넌트를 고르면
//   그 테넌트 관리자와 같은 메뉴" 수정이 사이드바에만 들어가고 여기엔 빠졌다.
import { composeAdminMenus } from '../adminlayout/menuComposition';
import { useMenuCategories } from '../adminlayout/menuCategories';

// 카드 아이콘 색 — 카테고리별 구분 (2026-08-25 피드백: 직원근태=파랑, 급여보상=초록 등.
// 탭 제목 색은 유지 — 아이콘에만 적용)
const CATEGORY_ICON_COLORS = {
  '직원·근태': '#2563eb', // 파랑
  '급여·보상': '#16a34a', // 초록
  '콜·영업': '#f59e0b',   // 주황
  '미팅': '#8b5cf6',      // 보라
  '계약': '#0ea5e9',      // 하늘
  '정산·재무': '#e11d48', // 로즈
  '상품·설정': '#0d9488', // 청록
  '운영': '#d97706',      // 갈색빛 주황
  '시스템': '#64748b',    // 회청
};

export function FeatureLauncher({ isMobile }) {
  const { permissions, permCodes } = useAppPermissions();
  const isAdmin = isAdminUser();
  const scope = useCompanyScope();
  const [tab, setTab] = useState('전체');
  const [query, setQuery] = useState('');
  const cats = useMenuCategories();

  // 카드 목록 — 사이드바와 동일한 권한 필터·카테고리·라벨 규칙 + 선택 회사 반영 (2026-09-03)
  const { cards, categories } = useMemo(() => {
    const { visibleGroups, groupOrder } = composeAdminMenus({ cats, permissions, permCodes, isAdmin, scope });
    const adminOnly = filterAdminOnlyMenus(ADMIN_ONLY_MENUS, isAdmin);
    const generalCards = visibleGroups.flatMap((g) => g.items.map((it) => ({ ...it, category: g.group })));

    const all = [
      ...generalCards,
      ...adminOnly.map((it) => ({ ...it, category: '시스템' })),
    ]
      // 상황판 자기 자신 카드는 제외 (지금 보고 있는 화면)
      .filter((c) => c.to !== '/admin/board')
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'ko'));

    // 탭 순서 = 사이드바 분류 순서(DB), 카드가 있는 분류만
    const present = new Set(all.map((c) => c.category).filter(Boolean));
    const order = groupOrder.filter((g) => g !== '현황' && present.has(g));
    return { cards: all, categories: order };
  }, [cats, permissions, permCodes, isAdmin, scope]);

  const q = query.trim().toLowerCase();
  const visible = cards.filter((c) => {
    if (tab !== '전체' && c.category !== tab) return false;
    if (!q) return true;
    return String(c.label).toLowerCase().includes(q) || String(c.desc || '').toLowerCase().includes(q);
  });

  return (
    <div className="mb-4">
      {/* 검색 — 탭과의 간격 40px (통일 기준, 2026-08-27) */}
      <div className={cn('relative mb-10', isMobile ? 'w-full' : 'max-w-[400px]')}>
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="기능 검색"
          className={cn(
            'w-full rounded-xl border border-[var(--border)] bg-[var(--bg-card)]',
            'pl-9 pr-3 py-2.5 text-[13px] text-[var(--text)] outline-none',
            'focus:border-[var(--text-muted)] transition-colors'
          )}
        />
      </div>

      {/* 카테고리 탭 */}
      <div className="flex gap-1 mb-3 overflow-x-auto overscroll-contain">
        {['전체', ...categories].map((c) => (
          <button
            key={c}
            onClick={() => setTab(c)}
            className={cn(
              'px-3 py-1.5 text-[13px] whitespace-nowrap border-b-2 bg-transparent cursor-pointer transition-colors',
              tab === c
                ? 'font-extrabold text-[var(--text)] border-[var(--text)]'
                : 'font-medium text-[var(--text-muted)] border-transparent hover:text-[var(--text-sub)]'
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {/* 카드 그리드 */}
      {visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-card)] p-6 text-sm text-[var(--text-muted)]">
          {q ? '검색 결과가 없습니다.' : '표시할 기능이 없습니다.'}
        </div>
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(230px, 1fr))' }}
        >
          {visible.map((c) => {
            const Icon = c.Icon;
            return (
              <Link
                key={`${c.category}-${c.to}`}
                to={c.to}
                className={cn(
                  'group flex flex-col no-underline rounded-2xl border border-[var(--border)] bg-[var(--bg-card)]',
                  'p-4 transition-shadow hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'
                )}
              >
                {Icon && (
                  <Icon
                    size={22}
                    // 다크 모드: 하늘색(#0ea5e9) 아이콘만 포인트색으로 (2026-09-28). 인라인 color 를 이기려고 ! 필요
                    className={cn('mb-3', CATEGORY_ICON_COLORS[c.category] === '#0ea5e9' && 'dark:!text-accent-400')}
                    strokeWidth={1.8}
                    style={{ color: CATEGORY_ICON_COLORS[c.category] || 'var(--text-sub)' }}
                  />
                )}
                <div className="text-[14px] font-extrabold text-[var(--text)] leading-snug">{c.label}</div>
                <div className="mt-1 text-[12px] leading-relaxed text-[var(--text-muted)] min-h-[34px]">
                  {c.desc || ''}
                </div>
                <div className="mt-2 flex items-center gap-1 text-[12px] font-bold text-[var(--text-sub)] group-hover:text-[var(--text)]">
                  바로가기 <ArrowRight size={13} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
